import {
  countryEn,
  countryCn,
  regionEn,
  regionCn,
} from '@/pages/opinion/constants';

/**
 * 地区/国家映射字典
 * 与写死常量同构，作为「接口数据 + 兜底常量」合并后的运行时字典。
 *
 * 数据来源（优先级从高到低）：
 *   ① cond 接口 / sessionStorage 中的 region_country
 *   ② 写死的兜底常量（countryEn/countryCn/regionEn/regionCn）
 */

export interface RegionCountryDict {
  /** 国家英文（小写缩写 → 英文名） */
  countryEn: Record<string, string>;
  /** 国家中文（小写缩写 → 中文名） */
  countryCn: Record<string, string>;
  /** 地区英文（大写缩写 → 英文名） */
  regionEn: Record<string, string>;
  /** 地区中文（大写缩写 → 中文名） */
  regionCn: Record<string, string>;
}

/**
 * 开关：是否使用接口数据（region_country）参与翻译。
 *
 * - true  → 走「新方案」：优先接口数据，缺失回退写死常量。
 * - false → 走「本地」：忽略接口数据，完全使用写死常量（便于对照测试）。
 *
 * 仅用于测试/联调，上线前应保持 true。
 */
export const USE_DYNAMIC_REGION_COUNTRY = true;

/**
 * 模块级单例 store：保存「接口数据」部分（不含兜底），
 * 跨 import 共享同一份引用，供同步翻译函数读取。
 */
let dynamicDict: RegionCountryDict = {
  countryEn: {},
  countryCn: {},
  regionEn: {},
  regionCn: {},
};

/**
 * 将 cond 接口返回的 region_country 结构「摊平」成与写死常量同构的四个映射。
 *
 * 大小写约定：
 *   - 国家 key：写死常量用小写（cn），接口 country_name_abbre 为大写（AO），需统一 toLowerCase。
 *   - 地区 key：写死常量用大写（AF），接口 region_key 为大写，保持一致。
 *
 * @param regionCountryList cond 接口 region_country 字段的原始数组
 * @returns 摊平后的四个映射（不含兜底）
 */
export function transformRegionCountryToDict(
  regionCountryList: any[],
): RegionCountryDict {
  const result: RegionCountryDict = {
    countryEn: {},
    countryCn: {},
    regionEn: {},
    regionCn: {},
  };

  (regionCountryList || []).forEach((region: any) => {
    const regionKey = region?.region_key; // 大写，如 'AF'
    if (region?.region_name_en) result.regionEn[regionKey] = region.region_name_en;
    if (region?.region_name_cn) result.regionCn[regionKey] = region.region_name_cn;

    (region?.country || []).forEach((c: any) => {
      // 写死常量中 country key 为「小写」缩写（如 'cn'）
      const key = (c?.country_name_abbre || '').toLowerCase();
      if (c?.country_name_en) result.countryEn[key] = c.country_name_en;
      if (c?.country_name_cn) result.countryCn[key] = c.country_name_cn;
    });
  });

  return result;
}

/**
 * 合并兜底常量与动态字典：接口数据覆盖兜底，未覆盖到的用兜底补全。
 */
function mergeWithFallback(dynamic: RegionCountryDict): RegionCountryDict {
  return {
    countryEn: { ...countryEn, ...dynamic.countryEn },
    countryCn: { ...countryCn, ...dynamic.countryCn },
    regionEn: { ...regionEn, ...dynamic.regionEn },
    regionCn: { ...regionCn, ...dynamic.regionCn },
  };
}

/**
 * 写入动态字典（接口数据），与兜底常量合并后缓存最终映射。
 * 开关关闭（USE_DYNAMIC_REGION_COUNTRY = false）时忽略写入，保持 store 为空。
 * @param next 接口数据摊平后的映射（可部分写入）
 */
export function setRegionCountryDict(next: Partial<RegionCountryDict>): void {
  if (!USE_DYNAMIC_REGION_COUNTRY) return;
  dynamicDict = {
    countryEn: { ...dynamicDict.countryEn, ...(next.countryEn || {}) },
    countryCn: { ...dynamicDict.countryCn, ...(next.countryCn || {}) },
    regionEn: { ...dynamicDict.regionEn, ...(next.regionEn || {}) },
    regionCn: { ...dynamicDict.regionCn, ...(next.regionCn || {}) },
  };
}

/**
 * 纯同步读取「兜底 + 接口」合并后的最终字典。
 * 翻译函数内部调用，缺失时自动回退写死常量。
 * 开关关闭时直接返回写死常量（不合并接口数据）。
 */
export function getRegionCountryDict(): RegionCountryDict {
  if (!USE_DYNAMIC_REGION_COUNTRY) {
    return {
      countryEn,
      countryCn,
      regionEn,
      regionCn,
    };
  }
  return mergeWithFallback(dynamicDict);
}

/**
 * 仅返回动态（接口）字典，不含兜底。用于判断 store 是否已注入。
 */
export function getDynamicRegionCountryDict(): RegionCountryDict {
  return dynamicDict;
}
